<script setup lang='ts'>
import {Button} from "@/components/ui/button"
import {Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle,} from "@/components/ui/card"
import {Input} from "@/components/ui/input"
import {Label} from "@/components/ui/label"
import {UserApi} from '@js-app/office/api/user-api'
import ErrorIcon from "@/vue/components/icons/ErrorIcon.vue";
import CloseEyeIcon from "@/vue/components/icons/CloseEyeIcon.vue";
import OpenEyeIcon from "@/vue/components/icons/ OpenEyeIcon.vue";
import {useRouter} from 'vue-router'
import {ref} from 'vue'
import { useStore } from "vuex";
import { useReCaptcha } from 'vue-recaptcha-v3'

const showPassword = ref(false)
const email = ref('')
const password = ref('')
const errors = ref<{ email: string | null, password: string | null, general: string | null }>({
  email: null,
  password: null,
  general: null
})
const isLoading = ref(false)
const router = useRouter()
const store = useStore();
const PHONE_REGEX = /^\+?[0-9\s-()]{10,15}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const { executeRecaptcha, recaptchaLoaded, instance } = useReCaptcha();
const LOGIN_USER = (payload: any) => store.dispatch('LOGIN_USER', payload);

async function loginWithCaptcha() {
  if (!validateForm()) {
    return
  }

  if (!(await recaptchaLoaded())) {
    errors.value.general = 'cabinet-login.errors.captcha_loading_failed';
    return;
  }

  isLoading.value = true;
  errors.value.general = null;

  try {
    const token = await executeRecaptcha('submit');

    if (!token) {
      throw new Error("Empty CAPTCHA token received.");
    }

    await sendLoginRequest(token);

  } catch (error: any) {

    let serverMessage: string | undefined;

    if (error && error.response && error.response.data) {
      const errorData = error.response.data.errors;
      serverMessage = errorData?.["g-recaptcha-response"]?.[0] || errorData?.Message || error.response.data.message;
    } else if (error.message === "Empty CAPTCHA token received.") {
      serverMessage = 'captcha_failed';
    } else {
      serverMessage = 'default_login_error';
    }

    errors.value.general = serverMessage ? `cabinet-login.errors.${serverMessage}` : 'default_login_error';

  } finally {
    isLoading.value = false
  }
}
function togglePasswordVisibility() {
  showPassword.value = !showPassword.value
}

function goToForgotPassword() {
  router.push({ name: 'forgot-password' })
}

function validateForm(): boolean {
  let isValid = true
  errors.value = { email: null, password: null, general: null }

  const loginValue = email.value.trim();

  if (!loginValue) {
    errors.value.email = 'cabinet-login.errors.email_required'
    isValid = false
  } else {
    const isEmailFormat =  loginValue.includes('@') && loginValue.includes('.');
    // Проверяем, начинается ли ввод с цифры ИЛИ с '+'
    const startsWithDigitOrPlus = /^[+\d]/.test(loginValue);

    // Если это похоже на email ИЛИ если это не начинается с '+' или цифры,
    // считаем это email и проверяем по правилам email.
    if (isEmailFormat || !startsWithDigitOrPlus) {
      if (!EMAIL_REGEX.test(loginValue)) {
        errors.value.email = 'cabinet-login.errors.email_invalid'
        isValid = false
      }
    } else {
      // Это телефонный номер (начинается с цифры или '+')

      // Логика очистки и проверки телефона остается корректной
      let cleanPhone = loginValue.replace(/[\s-()]/g, '');

      if (cleanPhone.startsWith('+')) {
        cleanPhone = cleanPhone.substring(1);
      }

      if (!/^\d{10,15}$/.test(cleanPhone)) {
        errors.value.email = 'cabinet-login.errors.login_invalid_phone_format'
        isValid = false
      }
    }
  }

  if (!password.value) {
    errors.value.password = 'cabinet-login.errors.password_required'
    isValid = false
  }

  return isValid
}

function cleanLoginValue(value: string): string {
  const loginValue = value.trim();
  if (EMAIL_REGEX.test(loginValue)) {
    return loginValue;
  }

  let cleaned = loginValue.replace(/[\s-()]/g, '');

  if (cleaned.startsWith('+')) {
    cleaned = cleaned.substring(1);
  }

  if (/^80\d{9}$/.test(cleaned)) {
    cleaned = `3${cleaned}`;
  }

  return cleaned;
}

async function sendLoginRequest(captchaToken: string) {
  const cleanedLogin = cleanLoginValue(email.value);
  try {
    const loginData = {
      login: cleanedLogin,
      password: password.value,
      'g-recaptcha-response': captchaToken,
    };

    const _authResponse = await UserApi.login(loginData)

    const payload = {
      accessToken: _authResponse.data.access_token,
      refreshToken: _authResponse.data.refresh_token,
    }

    await LOGIN_USER(payload);

    await router.push({
      name: _authResponse.data.need_otp ? 'login-otp' : 'cabinet',
      state: { phone_mask: _authResponse.data.phone_mask || null }
    });

    instance.value.hideBadge();

  } catch (error: any) {
    if (error && error.status === 401) {
      const serverMessage = error.data.errors.Message;
      errors.value.general = serverMessage ? `cabinet-login.errors.${serverMessage}` : 'default_login_error';
    }  else {
      errors.value.general = `cabinet-login.errors.default_login_error`;
    }
  } finally {
    isLoading.value = false
  }
}

</script>

<template>
  <div class="login">
    <div class="login__errors" v-if="errors.general">
      <Card class="w-[400px] mb-4 flex flex-row p-6 gap-5">
        <ErrorIcon class="h-5 w-5 text-red-600 flex-shrink-0" />
        <CardHeader class="w-full text-[#EF4444] flex flex-col p-0">
          <CardTitle class="text-sm font-medium flex gap-2">
            <span>{{ $t('cabinet-login.error_title') || 'Помилка входу' }}</span>
          </CardTitle>
          <CardDescription class="text-[#171717]">
            <span v-if="errors.general">{{ $t(`${errors.general}`) }}</span>
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
    <div class="login__card">
      <Card class="w-[400px]">
        <CardHeader>
          <CardTitle>{{ $t('cabinet-login.title') }}</CardTitle>
          <CardDescription>{{ $t('cabinet-login.description') }}</CardDescription>
        </CardHeader>

        <CardContent>
          <form @submit.prevent="login" class="flex flex-col gap-y-6">
            <div class="flex flex-col space-y-1.5">
              <Label for="email">{{ $t('cabinet-login.email') }}</Label>
              <Input
                  id="email"
                  v-model="email"
                  :class="{'border-red-500': errors.email}"
                  class="focus:outline-none focus-visible:ring-0 focus-visible:border-black"
              />
              <span v-if="errors.email" class="text-sm text-red-500">{{ $t(`${errors.email}`) }}</span>
            </div>

            <div class="flex flex-col space-y-1.5">
              <div class="flex items-center justify-between">
                <Label for="password">{{ $t('cabinet-login.password') }}</Label>
                <Label
                    for="password"
                    class="text-sm hover:underline cursor-pointer text-[#64748B]"
                    @click="goToForgotPassword"
                >
                  {{ $t('cabinet-login.forgot_password') }}
                </Label>
              </div>

              <div class="relative">
                <Input
                    :type="showPassword ? 'text' : 'password'"
                    id="password"
                    v-model="password"
                    :class="{'border-red-500': errors.password}"
                    class="pr-10 focus:outline-none focus-visible:ring-0 focus-visible:border-black w-full"
                />
                <button
                    type="button"
                    class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black"
                    @click="togglePasswordVisibility"
                    tabindex="-1"
                >
                  <CloseEyeIcon v-if="showPassword" />
                  <OpenEyeIcon v-else />
                </button>
              </div>
              <span v-if="errors.password" class="text-sm text-red-500">{{ $t(`${errors.password}`) }}</span>
            </div>

          </form>
        </CardContent>

        <CardFooter class="flex justify-between px-6">
          <Button class="w-full bg-[#64748B] transition-colors hover:bg-[#64748B] hover:opacity-75" @click.prevent="loginWithCaptcha" :disabled="isLoading">
            <span v-if="isLoading">Logging In...</span>
            <span v-else>{{ $t('cabinet-login.login_button') }}</span>
          </Button>
        </CardFooter>
      </Card>

      <div class="mt-4 w-[400px] text-left text-[12px] text-gray-600 leading-[20px]">
        <span>{{ $t('cabinet-login.policy_privacy.text') }}</span>
        <a href="#" class="text-[#64748B] underline hover:text-blue-700 font-medium">{{ $t('cabinet-login.policy_privacy.link_rule') }}</a>
        <span>{{ $t('cabinet-login.policy_privacy.link_separator') }}</span>
        <a href="#" class="text-[#64748B] underline hover:text-blue-700 font-medium">{{ $t('cabinet-login.policy_privacy.link_policy') }}</a>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  :deep(.login) {
    display: flex;
    min-height: min-content;
  }
</style>
